import { getSDK } from '@/store/sdkStore';
import { idbGet, idbSet, idbDelete } from '@/lib/storage/idb';
import { createLogger } from '@/lib/logger';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';

const log = createLogger('eventLog');

// Анти-лавина: warn «server error, queueing» — раз в 10с на один и тот же сбой.
let lastSendErrorWarn = 0;

// Есть ли активный JWT (авторизация): без токена события НЕ шлются на сервер
// (иначе 401-шум на форме входа) — остаются в durable-очереди до входа.
export function hasAuthToken(): boolean {
  return Boolean(
    useAuthStore.getState().token ||
    useCenterStore.getState().token,
  );
}

// Базы-как-схемы: без активной базы сервер отвечает 400 «no base selected».
// Журнал шлём только когда база выбрана (SSoT — centerStore.baseSlug).
function hasBase(): boolean {
  return Boolean(useCenterStore.getState().baseSlug);
}

// ============================================================================
// eventLog.ts — клиентский журнал регистрации (как в 1С)
// ============================================================================
// SSoT: сервер (время — серверное). Онлайн → LogEvent RPC; офлайн → очередь
// в IndexedDB (переживает перезагрузку), отправка при восстановлении сети.
// Очередь не привязана к baseId (события журнала — глобальные для центра).

export type EventLevel = 'debug' | 'info' | 'warning' | 'error';
export type EventCategory = 'auth' | 'data' | 'metadata' | 'file' | 'system' | 'sync' | 'ui' | 'security';

export interface LogEventInput {
  level: EventLevel;
  category: EventCategory;
  message: string;
  entityType?: string;
  entityCode?: string;
  objectId?: string;
  details?: string;
  /** код приложения-источника (клиент логирует за приложение); пусто = «Система»/ядро */
  app?: string;
}

// Тип приложения клиента (журнал регистрации): desktop (Wails) | pwa | web.
function detectClientApp(): string {
  if (typeof window === 'undefined') return 'web';
  const w = window as any;
  if (typeof w.wails !== 'undefined' || navigator.userAgent.includes('Wails')) return 'desktop';
  if (navigator.userAgent.includes('Electron')) return 'desktop';
  if (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true) return 'pwa';
  return 'web';
}

const CLIENT_APP = typeof window !== 'undefined' ? detectClientApp() : 'web';

const QUEUE_KEY = 'event_log_queue';

interface QueuedEvent extends LogEventInput {
  ts: number; // локальное время (для порядка; сервер перезапишет ts)
}

function getQueue(): Promise<QueuedEvent[]> {
  return idbGet<QueuedEvent[]>(QUEUE_KEY).then((q) => q || []);
}

function setQueue(q: QueuedEvent[]): Promise<void> {
  return idbSet(QUEUE_KEY, q);
}

// Отправка очереди на сервер (при восстановлении сети / по событию).
export async function flushEventLogQueue(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  if (!hasAuthToken()) return; // до входа не шлём (иначе 401-шум), очередь ждёт
  if (!hasBase()) return; // базы-как-схемы: без активной базы сервер 400, очередь ждёт
  const queue = await getQueue();
  if (queue.length === 0) return;
  const sdk = getSDK();
  let ok = 0;
  for (const ev of queue) {
    try {
      await sdk.eventLog.logEvent({
        level: ev.level,
        category: ev.category,
        message: ev.message,
        entityType: ev.entityType,
        entityCode: ev.entityCode,
        objectId: ev.objectId,
        details: ev.details || undefined,
        clientApp: CLIENT_APP,
        app: ev.app,
      });
      ok++;
    } catch (e: unknown) {
      log.warn('eventLog flush failed, keeping in queue', e);
      break;
    }
  }
  if (ok > 0) {
    await setQueue(queue.slice(ok));
  }
}

/**
 * Запись события в журнал регистрации.
 * Онлайн → сервер (SSoT); офлайн → durable queue в IndexedDB (flush при сети).
 * Ошибка сервера БРОСАЕТСЯ (не тихий фоллбэк) — вызывающий решает.
 */
export async function logEvent(input: LogEventInput): Promise<void> {
  // Лаунчер (база не выбрана): сервер отвечает 400 «no base selected» —
  // такие события не пишем ни на сервер, ни в очередь (это не ошибки).
  if (!useCenterStore.getState().baseSlug) return;
  if (hasAuthToken() && (typeof navigator === 'undefined' || navigator.onLine)) {
    const sdk = getSDK();
    try {
      await sdk.eventLog.logEvent({
        level: input.level,
        category: input.category,
        message: input.message,
        entityType: input.entityType,
        entityCode: input.entityCode,
        objectId: input.objectId,
        details: input.details || undefined,
        clientApp: CLIENT_APP,
        app: input.app,
      });
      return;
    } catch (e: unknown) {
      // Серверная ошибка: не теряем событие — кладём в очередь (офлайн-семантика).
      // Анти-лавина: warn пишем раз в 10с (перманентный сбой журнала не должен
      // флудить кольцо/журнал и лить неограниченно в очередь — она ограничена
      // 500 сверху, но без дедупа одинаковые сетевые сбои молотили бы её).
      const now = Date.now();
      if (now - lastSendErrorWarn >= 10000) {
        lastSendErrorWarn = now;
        const { centerUrl, baseSlug } = useCenterStore.getState();
        log.warn(`eventLog server error, queueing | center=${centerUrl || '(local)'} base=${baseSlug || '(none)'} err=${String((e as any)?.rawMessage || (e as any)?.message || e).slice(0, 200)}`);
      }
    }
  }
  // Без токена (до входа) или офлайн или ошибка сервера: durable queue
  const queue = await getQueue();
  queue.push({ ...input, ts: Date.now() });
  await setQueue(queue.slice(-500)); // предохранитель: не больше 500 в очереди
}

// Подписка на восстановление сети — автоматический flush очереди.
export function initEventLogFlush(): void {
  if (typeof window === 'undefined') return;
  const onOnline = () => { flushEventLogQueue(); };
  window.addEventListener('online', onOnline);
  window.addEventListener('focus', () => { flushEventLogQueue(); });
  // Стартовый flush (если события копились в прошлой сессии)
  setTimeout(() => { flushEventLogQueue(); }, 5000);
}

// ---- Удобные обёртки ----

export const eventLog = {
  debug: (category: EventCategory, message: string, extra?: Partial<LogEventInput>) =>
    logEvent({ level: 'debug', category, message, ...extra }),
  info: (category: EventCategory, message: string, extra?: Partial<LogEventInput>) =>
    logEvent({ level: 'info', category, message, ...extra }),
  warning: (category: EventCategory, message: string, extra?: Partial<LogEventInput>) =>
    logEvent({ level: 'warning', category, message, ...extra }),
  error: (category: EventCategory, message: string, extra?: Partial<LogEventInput>) =>
    logEvent({ level: 'error', category, message, ...extra }),
};
